import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Info, Star, MessageSquare, ListPlus, CalendarPlus, Clock,
  CheckCircle, ArrowUpCircle, AlertTriangle, Send, User, MapPin, DollarSign, Tag, Layers,
} from 'lucide-react';
import {
  KPICard, DomainCard, StatusBadge, SectionHeading, EmptyState, DetailRow,
  FilterToggle, FilterSearch, Skeleton,
  Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Tooltip, TooltipTrigger, TooltipContent, Input,
  ICON_PX, useToast,
} from '../ui';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { mockConsumerOverview, mockConsumerSignals } from '../../utils/mockData';
import { addToPriorityQueue } from '../../utils/api';

// Get a sortable datetime from either complaint or signal
function getItemDate(item) {
  if (item._type === 'signal') return item.receivedAt || '';
  return item.createdAt ? `${item.createdAt}T00:00:00Z` : '';
}

// Format date Outlook-style: "Today", "Yesterday", "Mon", or "Mar 25"
function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function buildFeedItems(complaints, signals, agingFilter) {
  const openComplaints = complaints
    .filter((c) => c.status !== 'resolved' && c.status !== 'escalated')
    .map((c) => ({ ...c, _type: 'complaint' }));

  const filteredComplaints = agingFilter === 'all'
    ? openComplaints
    : agingFilter === 'under3d'
      ? openComplaints.filter((c) => c.daysOpen < 3)
      : agingFilter === 'd3to7'
        ? openComplaints.filter((c) => c.daysOpen >= 3 && c.daysOpen <= 7)
        : openComplaints.filter((c) => c.daysOpen > 7);

  const signalItems = signals.map((s) => ({ ...s, _type: 'signal' }));

  // Sort by datetime descending
  return [...filteredComplaints, ...signalItems].sort((a, b) => {
    const da = getItemDate(a), db = getItemDate(b);
    return db.localeCompare(da);
  });
}

function ConsumerOverviewTab({ complaints, setComplaints, readOnly, onCreateComplaint }) {
  const { selectedUnitId } = useConsumerUnit();
  const data = mockConsumerOverview[selectedUnitId] || mockConsumerOverview.all;
  const signals = mockConsumerSignals[selectedUnitId] || mockConsumerSignals.all;
  const { toast } = useToast();

  const [agingFilter, setAgingFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [noteDialog, setNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const feedItems = useMemo(() => buildFeedItems(complaints, signals, agingFilter), [complaints, signals, agingFilter]);

  const unitFiltered = useMemo(() => {
    let items = feedItems;
    if (selectedUnitId !== 'all') items = items.filter((item) => item.unitId === selectedUnitId);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((item) =>
        (item.subject || item.title || '').toLowerCase().includes(q) ||
        (item.sender || item.source || '').toLowerCase().includes(q) ||
        (item.unitName || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [feedItems, selectedUnitId, searchQuery]);

  // Default select first item
  useEffect(() => {
    if (unitFiltered.length > 0 && (!selectedItem || !unitFiltered.find((i) => i.id === selectedItem.id))) {
      setSelectedItem(unitFiltered[0]);
    }
  }, [unitFiltered, selectedItem]);

  const updateComplaint = useCallback((id, updates) => {
    setComplaints((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    if (selectedItem?.id === id) setSelectedItem((prev) => prev ? { ...prev, ...updates } : null);
  }, [setComplaints, selectedItem]);

  if (!data) return <Skeleton.KPICard size="md" />;

  const { cpfiScore, googleRating, cahpsComposite, openComplaints: oc } = data;
  const aging = oc.aging;
  const agingOptions = [
    { value: 'all', label: 'All', count: oc.total },
    { value: 'under3d', label: '< 3d', count: aging.under3d },
    { value: 'd3to7', label: '3-7d', count: aging.d3to7 },
    { value: 'over7d', label: '> 7d', count: aging.over7d },
  ];

  const handleAddNote = () => {
    if (!selectedItem || !noteText.trim()) return;
    updateComplaint(selectedItem.id, { notes: [...(selectedItem.notes || []), noteText.trim()] });
    toast({ title: 'Note added' });
    setNoteDialog(false);
    setNoteText('');
  };

  const handleQueue = (item) => {
    addToPriorityQueue({
      title: item.subject || item.title,
      priority: item.urgency >= 5 ? 'critical' : item.urgency >= 4 ? 'high' : 'medium',
      category: 'Consumer', type: 'task',
      estimatedTime: 30, dueDate: new Date().toISOString(),
    }).catch(() => {});
    toast({ title: 'Added to Priority Queue' });
  };

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      {/* Key Metrics — compact row */}
      <div className="shrink-0">
        <SectionHeading
          size="sm" className="mb-1.5" title="Key Metrics"
          aiContext="consumer.satisfaction-scores"
          action={
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" maxWidth={220}>
                CPFI Quality Score measures care pathway friction (target: 80). Google rating from GMB API. CAHPS composite from latest survey upload.
              </TooltipContent>
            </Tooltip>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <KPICard size="sm" density="compact" accentPlacement="top" domain="consumer"
            label="CPFI Quality Score" value={cpfiScore.value}
            sublabel={`Target: ${cpfiScore.target}`}
            trend={cpfiScore.trend} />
          <KPICard size="sm" density="compact" accentPlacement="top" domain="consumer"
            label="Google Rating"
            value={<span className="flex items-center gap-1">{googleRating.value}<Star size={10} fill="var(--orange-warn)" stroke="var(--orange-warn)" /></span>}
            sublabel={`${googleRating.totalReviews.toLocaleString()} reviews`} />
          <KPICard size="sm" density="compact" accentPlacement="top" domain="consumer"
            label="CAHPS Composite" value={cahpsComposite.value}
            sublabel={`Nat'l avg: ${cahpsComposite.nationalAvg}`} />
        </div>
      </div>

      {/* Command Center — fills remaining viewport */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0">
          <SectionHeading
            size="sm" className="mb-1.5" title="Complaint & Signal Command Center"
            action={
              <div className="flex items-center gap-2">
                <FilterToggle value={agingFilter} onChange={setAgingFilter} options={agingOptions} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" maxWidth={220}>
                    Open complaints and consumer signals sorted by date. Filter by aging bucket or search.
                  </TooltipContent>
                </Tooltip>
              </div>
            }
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 flex-1 min-h-0">
          {/* Left: Feed list */}
          <div className="lg:col-span-2 flex flex-col rounded-lg border border-nn-border bg-nn-white overflow-hidden">
            {/* Search bar */}
            <div className="shrink-0 p-1.5 border-b border-nn-border">
              <FilterSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search..." size="sm" width="full" />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-1">
              {unitFiltered.length === 0 ? (
                <EmptyState title="No items" body="No items match this filter." size="sm" />
              ) : (
                unitFiltered.map((item) => {
                  const isSelected = selectedItem?.id === item.id;
                  const isComplaint = item._type === 'complaint';
                  const dateStr = formatDateShort(getItemDate(item));
                  return (
                    <DomainCard
                      key={item.id} domain="consumer" accentPlacement="left"
                      className={`cursor-pointer transition-shadow mb-0.5 ${isSelected ? 'ring-1 ring-aqua shadow-sm' : 'hover:shadow-sm'}`}
                      onClick={() => setSelectedItem(item)}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-1">
                          <StatusBadge variant={item.urgency >= 5 ? 'critical' : item.urgency >= 4 ? 'below-target' : item.urgency >= 3 ? 'watch' : 'info'}>
                            U{item.urgency}
                          </StatusBadge>
                          {item.isLegal && <StatusBadge variant="critical">Legal</StatusBadge>}
                          <StatusBadge variant={isComplaint ? 'info' : 'watch'}>
                            {isComplaint ? 'Complaint' : 'Signal'}
                          </StatusBadge>
                        </div>
                        <span className="text-[10px] text-text-muted shrink-0 ml-2">{dateStr}</span>
                      </div>
                      <p className="text-body-sm font-medium text-text-dark truncate">{item.subject || item.title}</p>
                      <div className="flex gap-2 text-[10px] text-text-muted mt-0.5">
                        {isComplaint ? <span>{item.source}</span> : <span>{item.sender}</span>}
                        {selectedUnitId === 'all' && <span>{item.unitName}</span>}
                      </div>
                    </DomainCard>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Detail panel */}
          <div className="lg:col-span-3 rounded-lg border border-nn-border bg-nn-white p-4 overflow-y-auto">
            {!selectedItem ? (
              <div className="flex items-center justify-center h-full text-text-muted text-body">
                Select a complaint or signal to view details
              </div>
            ) : selectedItem._type === 'complaint' ? (
              <ComplaintDetail
                item={selectedItem} readOnly={readOnly}
                onResolve={() => { updateComplaint(selectedItem.id, { status: 'resolved' }); toast({ title: 'Resolved' }); }}
                onEscalate={() => { updateComplaint(selectedItem.id, { status: 'escalated', urgency: 5 }); toast({ title: 'Escalated' }); }}
                onQueue={() => handleQueue(selectedItem)}
                onAddNote={() => { setNoteDialog(true); setNoteText(''); }}
              />
            ) : (
              <SignalDetail
                item={selectedItem} readOnly={readOnly}
                onQueue={() => handleQueue(selectedItem)}
                onCreateComplaint={() => { onCreateComplaint?.(selectedItem); toast({ title: 'Complaint created' }); }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <p className="text-body-sm text-text-muted mb-2">{selectedItem?.subject}</p>
          <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Enter note..." />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button variant="default" onClick={handleAddNote}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Complaint Detail Panel ──────────────────────────────── */
function ComplaintDetail({ item, readOnly, onResolve, onEscalate, onQueue, onAddNote }) {
  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="flex items-center gap-1.5 flex-wrap">
        <StatusBadge variant={item.urgency >= 5 ? 'critical' : item.urgency >= 4 ? 'below-target' : 'watch'}>
          Urgency {item.urgency}
        </StatusBadge>
        {item.isLegal && <StatusBadge variant="critical">Legal Escalation</StatusBadge>}
        <StatusBadge variant={item.status === 'resolved' ? 'on-track' : item.status === 'escalated' ? 'critical' : 'info'}>
          {item.status.replace('_', ' ')}
        </StatusBadge>
        {item.daysOpen > 7 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span><StatusBadge variant="critical"><AlertTriangle size={ICON_PX.xs} className="mr-0.5" />{item.daysOpen}d open</StatusBadge></span>
            </TooltipTrigger>
            <TooltipContent side="bottom" maxWidth={220}>Auto-escalated to Director visibility (over 7 days open)</TooltipContent>
          </Tooltip>
        )}
      </div>

      <h3 className="text-heading-sm font-bold text-text-dark leading-snug">{item.subject}</h3>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
        <DetailRow icon={Layers} label="Source" value={item.source} />
        <DetailRow icon={Tag} label="Category" value={item.category?.replace(/_/g, ' ')} />
        <DetailRow icon={User} label="Owner" value={item.owner || 'Unassigned'} highlight={!item.owner} />
        <DetailRow icon={Clock} label="Days Open" value={item.daysOpen} highlight={item.daysOpen > 7} />
        <DetailRow icon={MapPin} label="Unit" value={item.unitName} />
        <DetailRow icon={DollarSign} label="Est. Cost" value={item.estimatedCost > 0 ? `$${item.estimatedCost.toLocaleString()}` : 'N/A'} />
        <DetailRow icon={Tag} label="Created" value={item.createdAt} />
        <DetailRow icon={AlertTriangle} label="Legal" value={item.isLegal ? 'Yes — Urgency 5' : 'No'} highlight={item.isLegal} />
      </div>

      {item.notes && item.notes.length > 0 && (
        <div>
          <span className="text-caption font-bold text-text-muted">Activity Notes ({item.notes.length})</span>
          <div className="mt-1 border-l-2 border-nn-border pl-3 flex flex-col gap-1.5">
            {item.notes.map((n, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-[10px] text-text-muted shrink-0 mt-0.5">#{i + 1}</span>
                <p className="text-body-sm text-text-dark">{n}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!readOnly && item.status !== 'resolved' && item.status !== 'escalated' && (
        <div className="flex flex-wrap gap-2 mt-auto pt-3 border-t border-nn-border">
          <Button variant="success" size="sm" onClick={onResolve}>
            <CheckCircle size={ICON_PX.sm} className="mr-1" /> Resolve
          </Button>
          <Button variant="danger" size="sm" onClick={onEscalate}>
            <ArrowUpCircle size={ICON_PX.sm} className="mr-1" /> Escalate
          </Button>
          <Button variant="default" size="sm" onClick={onQueue}>
            <ListPlus size={ICON_PX.sm} className="mr-1" /> Queue
          </Button>
          <Button variant="default" size="sm" onClick={onAddNote}>
            <MessageSquare size={ICON_PX.sm} className="mr-1" /> Note
          </Button>
          <Button variant="default" size="sm" onClick={() => {}}>
            <CalendarPlus size={ICON_PX.sm} className="mr-1" /> Schedule
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─── Signal Detail Panel ─────────────────────────────────── */
function SignalDetail({ item, readOnly, onQueue, onCreateComplaint }) {
  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="flex items-center gap-1.5 flex-wrap">
        <StatusBadge variant={item.urgency >= 5 ? 'critical' : item.urgency >= 4 ? 'below-target' : 'watch'}>
          Urgency {item.urgency}
        </StatusBadge>
        {item.isLegal && <StatusBadge variant="critical">Legal Escalation</StatusBadge>}
        <StatusBadge variant="info">{item.classification?.replace(/_/g, ' ')}</StatusBadge>
      </div>

      <h3 className="text-heading-sm font-bold text-text-dark leading-snug">{item.title}</h3>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
        <DetailRow icon={User} label="From" value={item.sender} />
        <DetailRow icon={Clock} label="Received" value={new Date(item.receivedAt).toLocaleString()} />
        <DetailRow icon={MapPin} label="Unit" value={item.unitName} />
        <DetailRow icon={Tag} label="Classification" value={item.classification?.replace(/_/g, ' ')} />
        <DetailRow icon={Layers} label="Confidence" value={`${Math.round(item.confidence * 100)}%`} highlight={item.confidence < 0.85} />
        <DetailRow icon={AlertTriangle} label="Legal" value={item.isLegal ? 'Yes — Auto-promoted to Queue' : 'No'} highlight={item.isLegal} />
      </div>

      {item.entities && Object.keys(item.entities).length > 0 && (
        <div>
          <span className="text-caption font-bold text-text-muted">Extracted Entities</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {Object.entries(item.entities).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface text-[11px]">
                <span className="text-text-muted capitalize">{k}:</span>
                <span className="text-text-dark font-medium">{v}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <span className="text-caption font-bold text-text-muted">Signal Detail</span>
        <div className="mt-1 p-2.5 rounded-lg bg-surface text-body-sm text-text-dark leading-relaxed">
          <p className="font-medium mb-1">{item.title}</p>
          <p>From: {item.sender}</p>
          <p>Subject: {item.title}</p>
          <p>Classification: {item.classification?.replace(/_/g, ' ')} ({Math.round(item.confidence * 100)}% confidence)</p>
          {item.isLegal && <p className="text-red-critical font-medium mt-1">Legal escalation — auto-promoted to Priority Queue</p>}
        </div>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap gap-2 mt-auto pt-3 border-t border-nn-border">
          <Button variant="default" size="sm" onClick={onCreateComplaint}>
            <Send size={ICON_PX.sm} className="mr-1" /> Create Complaint
          </Button>
          <Button variant="default" size="sm" onClick={onQueue}>
            <ListPlus size={ICON_PX.sm} className="mr-1" /> Add to Queue
          </Button>
          <Button variant="default" size="sm" onClick={() => {}}>
            <CalendarPlus size={ICON_PX.sm} className="mr-1" /> Schedule
          </Button>
        </div>
      )}
    </div>
  );
}


export default ConsumerOverviewTab;
